import { ArrowRight, Bot, Zap, Target, BarChart3, Shield, MessageSquare, GitBranch, Play, Check, X, ChevronRight, Globe, Layers, TrendingUp, Users, Star, Phone, Link2, Smartphone, ScanLine } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, useMotionValue, useSpring, useReducedMotion } from "framer-motion";
import { useEffect, useRef, useState, useCallback } from "react";
import { ScoringWidget } from "@/components/scoring/ScoringWidget";

// ─── Animation Variants ────────────────────────────────────

const fadeUp = {
  hidden: { opacity: 0, y: 20, willChange: 'opacity, transform' as const },
  visible: (i: number = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.5, delay: i * 0.08, ease: 'easeOut' },
  }),
};

const stagger = { visible: { transition: { staggerChildren: 0.06 } } };

const scaleIn = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.5, ease: [0.25, 0.4, 0.25, 1] } },
};

// ─── Mouse Follow Glow ────────────────────────────────────

function MouseGlow() {
  const prefersReducedMotion = useReducedMotion();
  const [isDesktop, setIsDesktop] = useState(false);

  const mouseX = useMotionValue(-1000);
  const mouseY = useMotionValue(-1000);
  const springX = useSpring(mouseX, { stiffness: 80, damping: 20, mass: 0.5 });
  const springY = useSpring(mouseY, { stiffness: 80, damping: 20, mass: 0.5 });

  useEffect(() => {
    // Only enable on devices that support real hover (desktop pointer)
    const mq = window.matchMedia('(hover: hover) and (pointer: fine)');
    const update = () => setIsDesktop(mq.matches);
    update();
    mq.addEventListener?.('change', update);
    return () => mq.removeEventListener?.('change', update);
  }, []);

  useEffect(() => {
    if (!isDesktop || prefersReducedMotion) return;
    const handleMove = (e: MouseEvent) => {
      mouseX.set(e.clientX - 300);
      mouseY.set(e.clientY - 300);
    };
    window.addEventListener('mousemove', handleMove);
    return () => window.removeEventListener('mousemove', handleMove);
  }, [isDesktop, prefersReducedMotion, mouseX, mouseY]);

  if (!isDesktop || prefersReducedMotion) return null;

  return (
    <motion.div
      aria-hidden="true"
      className="fixed top-0 left-0 w-[600px] h-[600px] rounded-full pointer-events-none z-0"
      style={{
        x: springX,
        y: springY,
        background: 'radial-gradient(circle, rgba(52, 211, 153, 0.08) 0%, rgba(52, 211, 153, 0.03) 40%, rgba(52, 211, 153, 0) 70%)',
        filter: 'blur(80px)',
      }}
    />
  );
}

// ─── Typewriter Effect ─────────────────────────────────────

function TypewriterText({ words }: { words: string[] }) {
  const prefersReducedMotion = useReducedMotion();
  const [currentWord, setCurrentWord] = useState(0);
  const [currentChar, setCurrentChar] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (prefersReducedMotion) return;
    const word = words[currentWord];
    const speed = isDeleting ? 40 : 80;

    if (!isDeleting && currentChar === word.length) {
      const hold = setTimeout(() => setIsDeleting(true), 1500);
      return () => clearTimeout(hold);
    }

    if (isDeleting && currentChar === 0) {
      setIsDeleting(false);
      setCurrentWord((prev) => (prev + 1) % words.length);
      return;
    }

    const timeout = setTimeout(() => {
      setCurrentChar(prev => isDeleting ? prev - 1 : prev + 1);
    }, speed);

    return () => clearTimeout(timeout);
  }, [currentChar, isDeleting, currentWord, words, prefersReducedMotion]);

  if (prefersReducedMotion) {
    return (
      <span className="bg-gradient-to-r from-emerald-300 to-teal-300 bg-clip-text text-transparent">
        {words[0]}
      </span>
    );
  }

  return (
    <span className="bg-gradient-to-r from-emerald-300 to-teal-300 bg-clip-text text-transparent">
      {words[currentWord].substring(0, currentChar)}
      <span className="animate-pulse text-emerald-400">|</span>
    </span>
  );
}

// ─── Scrolling Ticker ──────────────────────────────────────

function LiveTicker() {
  const items = [
    'Sarah M. from Austin, TX — Qualified (Auto Insurance)',
    'James R. from Phoenix, AZ — Converted (Solar)',
    'Maria L. from Miami, FL — Qualified (Mortgage)',
    'David K. from Denver, CO — Routed (Health)',
    'Emily W. from Seattle, WA — Converted (Auto)',
    'Chris P. from Atlanta, GA — Qualified (Solar)',
    'Lisa T. from Chicago, IL — Converted (Mortgage)',
    'Ryan B. from Portland, OR — Qualified (Health)',
  ];

  return (
    <div className="overflow-hidden py-3 border-y border-slate-800/50 bg-slate-900/30">
      <div className="flex animate-marquee whitespace-nowrap">
        {[...items, ...items].map((item, i) => (
          <span key={i} className="mx-6 text-xs text-slate-500 flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
            {item}
          </span>
        ))}
      </div>
    </div>
  );
}

// ─── Live Activity Ticker (floating pill) ─────────────────

function LiveActivityTicker() {
  const activities = [
    'Sarah from TX just qualified',
    'Mike from CA completed a flow',
    'Jessica from NY got matched to 3 offers',
    'David from FL converted in 45 seconds',
    'Emily from WA started a new flow',
    'Marcus from IL just qualified',
    'Ashley from GA got routed to top buyer',
    'Kevin from OH completed qualification',
    'Rachel from NC converted in 28 seconds',
    'Tyler from AZ matched to 2 offers',
  ];
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setIndex((prev) => (prev + 1) % activities.length);
    }, 4000);
    return () => clearInterval(id);
  }, [activities.length]);

  return (
    <div className="fixed top-20 left-1/2 -translate-x-1/2 z-30 pointer-events-none hidden sm:flex">
      <AnimatePresence mode="wait">
        <motion.div
          key={index}
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -12 }}
          transition={{ duration: 0.35, ease: 'easeOut' }}
          className="flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-slate-800/90 border border-slate-700/60 backdrop-blur-md shadow-lg shadow-black/20"
        >
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
          </span>
          <span className="text-xs text-slate-300 font-medium whitespace-nowrap">
            {activities[index]}
          </span>
          <span className="text-[10px] text-slate-500 whitespace-nowrap">just now</span>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

// ─── Floating Badge ────────────────────────────────────────

function FloatingBadge({ children, className, delay = 0 }: { children: React.ReactNode; className?: string; delay?: number }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1, y: [0, -8, 0] }}
      transition={{ opacity: { duration: 0.5, delay }, scale: { duration: 0.5, delay }, y: { duration: 3, repeat: Infinity, ease: 'easeInOut', delay } }}
      className={`absolute px-3 py-1.5 rounded-full bg-slate-800/80 border border-slate-700/50 backdrop-blur-sm text-xs font-medium shadow-lg ${className}`}
    >
      {children}
    </motion.div>
  );
}

// ─── Animated Counter ──────────────────────────────────────

function AnimatedNumber({ target, suffix = "", prefix = "" }: { target: number; suffix?: string; prefix?: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const [display, setDisplay] = useState(`${prefix}0${suffix}`);
  const hasAnimated = useRef(false);

  useEffect(() => {
    if (!ref.current || hasAnimated.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated.current) {
          hasAnimated.current = true;
          const start = performance.now();
          const duration = 1500;
          const step = (now: number) => {
            const progress = Math.min((now - start) / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3); // ease-out cubic
            const current = Math.round(eased * target);
            setDisplay(`${prefix}${current.toLocaleString()}${suffix}`);
            if (progress < 1) requestAnimationFrame(step);
          };
          requestAnimationFrame(step);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target, prefix, suffix]);

  return <span ref={ref}>{display}</span>;
}

// ─── Live Graph Component ──────────────────────────────────

function LiveConversionGraph() {
  const [bars, setBars] = useState([65, 45, 78, 52, 88, 70, 95, 60, 82, 73, 90, 85]);
  const [activeLine, setActiveLine] = useState(0);
  const colors = ['bg-emerald-500', 'bg-teal-500', 'bg-cyan-500', 'bg-blue-500', 'bg-indigo-500', 'bg-violet-500', 'bg-emerald-400', 'bg-teal-400', 'bg-cyan-400', 'bg-blue-400', 'bg-indigo-400', 'bg-violet-400'];

  useEffect(() => {
    const interval = setInterval(() => {
      setBars(prev => prev.map(v => Math.max(20, Math.min(100, v + (Math.random() - 0.4) * 15))));
      setActiveLine(prev => (prev + 1) % 12);
    }, 1500);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="relative h-40 flex items-end gap-1.5 px-2">
      {bars.map((height, i) => (
        <div
          key={i}
          className={`flex-1 rounded-t-sm transition-all duration-700 ease-out ${i === activeLine ? `${colors[i]} shadow-lg opacity-100` : `${colors[i]} opacity-40`}`}
          style={{ height: `${height}%` }}
        />
      ))}
    </div>
  );
}

function DonutChart() {
  const [segments] = useState([35, 25, 20, 20]);
  const segColors = ['#10b981', '#3b82f6', '#8b5cf6', '#f59e0b'];
  const labels = ['Qualified', 'Pending', 'Routed', 'New'];
  let cumulative = 0;

  return (
    <div className="flex items-center gap-4">
      <svg viewBox="0 0 36 36" className="w-20 h-20">
        {segments.map((seg, i) => {
          const dash = seg;
          const offset = 100 - cumulative;
          cumulative += seg;
          return (
            <circle key={i} cx="18" cy="18" r="15.9" fill="none"
              stroke={segColors[i]} strokeWidth="3"
              strokeDasharray={`${dash} ${100 - dash}`}
              strokeDashoffset={offset}
              className="transition-all duration-1000"
            />
          );
        })}
        <text x="18" y="18.5" textAnchor="middle" className="fill-white text-[6px] font-bold">85%</text>
        <text x="18" y="22" textAnchor="middle" className="fill-slate-500 text-[3px]">qualified</text>
      </svg>
      <div className="space-y-1">
        {labels.map((label, i) => (
          <div key={label} className="flex items-center gap-2 text-xs">
            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: segColors[i] }} />
            <span className="text-slate-400">{label}</span>
            <span className="text-white font-medium ml-auto">{segments[i]}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function MiniLineChart() {
  const points = [30, 45, 35, 55, 48, 65, 58, 72, 68, 80, 75, 88];
  const max = 100;
  const width = 200;
  const height = 50;
  const pathData = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${(i / (points.length - 1)) * width} ${height - (p / max) * height}`).join(' ');

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-12">
      <defs>
        <linearGradient id="lineGrad" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#10b981" />
          <stop offset="100%" stopColor="#3b82f6" />
        </linearGradient>
        <linearGradient id="areaGrad" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#10b981" stopOpacity="0.2" />
          <stop offset="100%" stopColor="#10b981" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={`${pathData} L ${width} ${height} L 0 ${height} Z`} fill="url(#areaGrad)" />
      <path d={pathData} fill="none" stroke="url(#lineGrad)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={width} cy={height - (points[points.length - 1] / max) * height} r="3" fill="#10b981" className="animate-pulse" />
    </svg>
  );
}

// ─── Animated Flow Diagram ─────────────────────────────────

function FlowDiagram() {
  const [activeNode, setActiveNode] = useState(0);
  const nodes = ['Engage', 'Collect', 'Qualify', 'Route'];

  useEffect(() => {
    const interval = setInterval(() => setActiveNode(prev => (prev + 1) % 4), 2000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="flex items-center justify-center gap-2">
      {nodes.map((node, i) => (
        <div key={node} className="flex items-center gap-2">
          <motion.div
            animate={{
              scale: i === activeNode ? 1.1 : 1,
              borderColor: i === activeNode ? 'rgb(52, 211, 153)' : 'rgb(51, 65, 85)',
              backgroundColor: i <= activeNode ? 'rgba(52, 211, 153, 0.1)' : 'transparent',
            }}
            transition={{ duration: 0.3 }}
            className="px-3 py-1.5 rounded-lg border text-xs font-medium text-slate-300"
          >
            {node}
          </motion.div>
          {i < 3 && (
            <motion.div
              animate={{ opacity: i < activeNode ? 1 : 0.3, scaleX: i < activeNode ? 1 : 0.5 }}
              className="text-emerald-400"
            >
              <ChevronRight className="h-3 w-3" />
            </motion.div>
          )}
        </div>
      ))}
    </div>
  );
}

// ─── Live Chat Demo ────────────────────────────────────────

function ChatDemo() {
  return (
    <div className="w-full max-w-sm mx-auto">
      <div className="rounded-2xl border border-slate-700/80 bg-slate-900/90 backdrop-blur-xl shadow-2xl shadow-black/50 overflow-hidden">
        <ScoringWidget
          chatbotId="11111111-1111-4111-8111-111111111111"
          entryIntent={{ primary_vertical: 'auto_insurance', source: 'landing_page', confidence: 0.5 }}
          embedded={true}
          brandName="IntentDrive"
          brandTagline="Try it live — answer a few questions"
        />
      </div>
    </div>
  );
}

// ─── Dashboard Preview ─────────────────────────────────────

function DashboardPreview() {
  return (
    <div className="rounded-xl border border-slate-700/60 bg-slate-900/90 backdrop-blur-xl overflow-hidden shadow-2xl shadow-black/40">
      {/* Dashboard header */}
      <div className="border-b border-slate-800 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full bg-red-500/80" />
            <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/80" />
            <div className="w-2.5 h-2.5 rounded-full bg-green-500/80" />
          </div>
          <div className="flex items-center gap-2">
            <Target className="h-3.5 w-3.5 text-emerald-400" />
            <span className="text-xs font-medium text-slate-400">IntentDrive Dashboard</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded-full">Live</span>
        </div>
      </div>

      <div className="p-4 space-y-3">
        {/* Stat cards row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <MiniStat label="Conversations" value="12,847" change="+23%" color="emerald" />
          <MiniStat label="Qualified" value="8,412" change="+18%" color="blue" />
          <MiniStat label="Converted" value="3,291" change="+31%" color="violet" />
          <MiniStat label="Revenue" value="$47.2K" change="+42%" color="amber" />
        </div>

        {/* Charts row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {/* Bar chart */}
          <div className="bg-slate-800/50 rounded-lg p-3">
            <div className="text-xs text-slate-500 mb-2">Conversion Rate — 30d</div>
            <LiveConversionGraph />
          </div>

          {/* Donut + line */}
          <div className="space-y-3">
            <div className="bg-slate-800/50 rounded-lg p-3">
              <div className="text-xs text-slate-500 mb-2">Lead Distribution</div>
              <DonutChart />
            </div>
          </div>
        </div>

        {/* Revenue trend line */}
        <div className="bg-slate-800/50 rounded-lg p-3">
          <div className="flex items-center justify-between mb-1">
            <div className="text-xs text-slate-500">Revenue Trend</div>
            <div className="text-xs text-emerald-400 font-medium">+42% MoM</div>
          </div>
          <MiniLineChart />
        </div>

        {/* Flow diagram */}
        <div className="bg-slate-800/50 rounded-lg p-3">
          <div className="text-xs text-slate-500 mb-2">Active Flow Pipeline</div>
          <FlowDiagram />
        </div>
      </div>
    </div>
  );
}

const statColors: Record<string, string> = {
  emerald: 'text-emerald-400',
  blue: 'text-blue-400',
  violet: 'text-violet-400',
  amber: 'text-amber-400',
};

function MiniStat({ label, value, change, color = 'emerald' }: { label: string; value: string; change: string; color?: string }) {
  return (
    <div className="bg-slate-800/50 rounded-lg p-2">
      <div className="text-[10px] text-slate-500">{label}</div>
      <div className="text-sm font-bold text-white mt-0.5">{value}</div>
      <div className={`text-[10px] ${statColors[color] || 'text-emerald-400'} font-medium`}>{change}</div>
    </div>
  );
}

// ─── Grid Background ───────────────────────────────────────

function GridBackground() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      <div className="absolute inset-0" style={{
        backgroundImage: `radial-gradient(circle at 1px 1px, rgba(148,163,184,0.05) 1px, transparent 0)`,
        backgroundSize: '40px 40px',
      }} />
      {/* Animated gradient lines */}
      <div className="absolute top-0 left-1/2 w-px h-full bg-gradient-to-b from-transparent via-emerald-500/10 to-transparent" />
      <div className="absolute top-1/2 left-0 w-full h-px bg-gradient-to-r from-transparent via-emerald-500/10 to-transparent" />
    </div>
  );
}

// ─── Main Page ─────────────────────────────────────────────

const Index = () => {
  return (
    <div className="min-h-screen bg-slate-950 text-white overflow-x-hidden">
      <GridBackground />
      <MouseGlow />
      <LiveActivityTicker />

      {/* Ambient glow */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-[-20%] left-[15%] w-[800px] h-[800px] bg-emerald-500/[0.07] rounded-full blur-[150px]" />
        <div className="absolute bottom-[-10%] right-[5%] w-[600px] h-[600px] bg-blue-500/[0.06] rounded-full blur-[120px]" />
        <div className="absolute top-[40%] right-[30%] w-[400px] h-[400px] bg-violet-500/[0.05] rounded-full blur-[100px]" />
        <div className="absolute top-[60%] left-[5%] w-[500px] h-[500px] bg-cyan-500/[0.04] rounded-full blur-[130px]" />
        <div className="absolute top-[80%] right-[40%] w-[400px] h-[400px] bg-rose-500/[0.03] rounded-full blur-[100px]" />
      </div>

      {/* Nav */}
      <header className="relative z-20 border-b border-slate-800/50 backdrop-blur-md bg-slate-950/80">
        <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }} className="flex justify-between items-center">
            <Link to="/" className="flex items-center gap-2">
              <img src="/logo.jpg" alt="IntentDrive.ai" className="h-10 sm:h-14 w-auto rounded" style={{ filter: 'brightness(1.2) sepia(1) hue-rotate(100deg) saturate(2)' }} />
              <span className="font-bold text-lg sm:text-xl tracking-tight">IntentDrive</span>
              <span className="text-[10px] text-emerald-400 font-bold tracking-wider uppercase">.ai</span>
            </Link>
            <div className="flex items-center gap-3">
              <Link to="/compare">
                <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white">Compare</Button>
              </Link>
              <Link to="/portal/auth">
                <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white">Login</Button>
              </Link>
              <Link to="/portal/signup">
                <Button size="sm" className="bg-emerald-500 hover:bg-emerald-600 text-white shadow-lg shadow-emerald-500/20">
                  Start Free <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </header>

      <main className="relative z-10 container px-4 sm:px-6 mx-auto">

        {/* ═══ HERO ═══ */}
        <section className="pt-12 sm:pt-20 pb-16 sm:pb-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left: Copy */}
            <motion.div initial="hidden" animate="visible" variants={stagger} className="space-y-6">
              <motion.div variants={fadeUp} custom={0} className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs font-medium">
                <Zap className="h-3 w-3" /> Revenue Routing Engine
              </motion.div>

              <motion.h1 variants={fadeUp} custom={1} className="text-3xl sm:text-4xl md:text-6xl font-bold leading-[1.1] tracking-tight">
                <span className="text-white">Make Every Click </span>
                <br />
                <TypewriterText words={['Worth More Money', 'a Revenue Event', 'Multi-Offer Loop', 'Fully Monetized']} />
              </motion.h1>

              <motion.p variants={fadeUp} custom={2} className="text-lg text-slate-400 leading-relaxed max-w-lg">
                The decision layer between your traffic and your buyers. IntentDrive detects intent, pivots rejected users into new offers, and routes every session to its highest-paying destination — in real time.
              </motion.p>

              <motion.div variants={fadeUp} custom={3} className="flex flex-col sm:flex-row items-start sm:items-center gap-3 pt-2">
                <Link to="/portal/signup">
                  <Button size="lg" className="bg-emerald-500 hover:bg-emerald-600 text-white px-6 py-5 shadow-xl shadow-emerald-500/20 hover:shadow-emerald-500/30 transition-all hover:scale-[1.02]">
                    Start Building Free <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <Link to="/try">
                  <Button variant="ghost" size="lg" className="text-slate-400 hover:text-white px-6 py-5">
                    <Play className="mr-2 h-4 w-4" /> Try Live Demo
                  </Button>
                </Link>
              </motion.div>

              <motion.div variants={fadeUp} custom={4} className="flex flex-wrap items-center gap-3 sm:gap-6 pt-4 text-xs sm:text-sm text-slate-500">
                <span className="flex items-center gap-1.5"><Check className="h-3.5 w-3.5 text-emerald-400" /> No credit card</span>
                <span className="flex items-center gap-1.5"><Check className="h-3.5 w-3.5 text-emerald-400" /> 5 min setup</span>
                <span className="flex items-center gap-1.5"><Check className="h-3.5 w-3.5 text-emerald-400" /> Free tier</span>
              </motion.div>
            </motion.div>

            {/* Right: Live dashboard + chat — hidden on mobile to prevent overflow */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="relative hidden lg:block"
            >
              <DashboardPreview />
              {/* Floating chat widget */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 1 }}
                className="absolute -bottom-8 -left-8 w-72 z-10 hidden lg:block"
              >
                <ChatDemo />
              </motion.div>
              {/* Floating badges */}
              <FloatingBadge className="top-4 -right-4 hidden lg:flex" delay={1.5}>
                <span className="text-emerald-400 mr-1">10x</span> <span className="text-slate-400">Faster Routing</span>
              </FloatingBadge>
              <FloatingBadge className="-top-6 left-1/4 hidden lg:flex" delay={2}>
                <span className="text-blue-400 mr-1">99.9%</span> <span className="text-slate-400">Accuracy</span>
              </FloatingBadge>
              <FloatingBadge className="top-1/3 -right-12 hidden lg:flex" delay={2.5}>
                <span className="text-violet-400 mr-1">0ms</span> <span className="text-slate-400">AI in Decisions</span>
              </FloatingBadge>
            </motion.div>
          </div>
        </section>

        {/* ═══ LIVE TICKER ═══ */}
        <LiveTicker />

        {/* ═══ METRICS BAR ═══ */}
        <motion.section
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0, margin: "0px 0px -50px 0px" }}
          variants={stagger}
          className="py-16 border-y border-slate-800/50"
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <MetricCard value={<AnimatedNumber target={340} suffix="%" />} label="Avg RPUS Lift" icon={<TrendingUp className="h-5 w-5" />} color="text-emerald-400" />
            <MetricCard value={<AnimatedNumber target={5} suffix="x" />} label="Multi-Offer Stack" icon={<Layers className="h-5 w-5" />} color="text-blue-400" />
            <MetricCard value={<AnimatedNumber target={50} suffix="M+" />} label="Sessions Routed" icon={<Target className="h-5 w-5" />} color="text-violet-400" />
            <MetricCard value={<AnimatedNumber target={98} suffix="%" />} label="Routing Accuracy" icon={<MessageSquare className="h-5 w-5" />} color="text-amber-400" />
          </div>
        </motion.section>

        {/* ═══ HOW IT WORKS ═══ */}
        <section className="py-24">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0, margin: "0px 0px -50px 0px" }} variants={stagger}>
            <motion.div variants={fadeUp} className="text-center mb-16">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-800 text-slate-400 text-xs font-medium mb-4">How It Works</div>
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">
                One Click. <span className="text-emerald-400">Multiple Monetization Paths.</span>
              </h2>
              <p className="text-lg text-slate-400 max-w-2xl mx-auto">Traditional funnels give you one shot. IntentDrive turns every session into a revenue loop — pivoting rejected users into new verticals automatically.</p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {[
                { n: '01', title: 'Detect Intent', desc: 'Capture vertical, page context, and answers in seconds. Build a user profile on the fly.', icon: <Target className="h-5 w-5" />, gradient: 'from-emerald-500 to-teal-500' },
                { n: '02', title: 'Route by Payout', desc: 'Deterministic rules select the highest-paying destination for each user profile in real time.', icon: <TrendingUp className="h-5 w-5" />, gradient: 'from-blue-500 to-cyan-500' },
                { n: '03', title: 'Pivot on Reject', desc: 'Auto → renter → loan → credit. Rejected users stay engaged and generate revenue from the next offer.', icon: <GitBranch className="h-5 w-5" />, gradient: 'from-violet-500 to-purple-500' },
                { n: '04', title: 'Trigger Call or Lead', desc: 'High-intent sessions fire a live call to Ringba/Retreaver. Lower intent gets form submission.', icon: <Zap className="h-5 w-5" />, gradient: 'from-amber-500 to-orange-500' },
              ].map((step, i) => (
                <motion.div key={step.n} variants={fadeUp} custom={i} className="group relative">
                  <div className="p-5 rounded-xl bg-slate-900/50 border border-slate-800 hover:border-slate-600 transition-all duration-300 h-full">
                    <div className="text-5xl font-black text-slate-800/50 group-hover:text-slate-800 transition-colors absolute top-3 right-4">{step.n}</div>
                    <div className={`inline-flex items-center justify-center w-10 h-10 rounded-lg bg-gradient-to-br ${step.gradient} text-white shadow-lg mb-4`}>{step.icon}</div>
                    <h3 className="text-lg font-semibold mb-2">{step.title}</h3>
                    <p className="text-sm text-slate-400 leading-relaxed">{step.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </section>

        {/* ═══ HOW IT'S DIFFERENT ═══ */}
        <section className="py-24 border-t border-slate-800/50">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0, margin: "0px 0px -50px 0px" }} variants={stagger}>
            <motion.div variants={fadeUp} className="text-center mb-16">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-800 text-slate-400 text-xs font-medium mb-4">How It's Different</div>
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">
                Not a Funnel. Not a Chatbot. <span className="text-emerald-400">A Revenue Engine.</span>
              </h2>
              <p className="text-lg text-slate-400 max-w-2xl mx-auto">See what changes when you stop thinking in clicks and start thinking in sessions.</p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                {
                  oldLabel: 'Traditional Funnels',
                  newLabel: 'IntentDrive',
                  oldText: '1 click \u2192 1 offer \u2192 bounce \u2192 lost forever',
                  newText: '1 click \u2192 score \u2192 present \u2192 reject \u2192 pivot \u2192 convert \u2192 expand \u2192 $$$',
                },
                {
                  oldLabel: 'Lead Distribution',
                  newLabel: 'IntentDrive',
                  oldText: 'Receives leads AFTER they\u2019re created',
                  newText: 'Creates leads AND maximizes their value',
                },
                {
                  oldLabel: 'Chatbots',
                  newLabel: 'IntentDrive',
                  oldText: 'Engagement tool for SaaS support',
                  newText: 'Revenue engine for performance marketing',
                },
              ].map((card, i) => (
                <motion.div
                  key={card.oldLabel}
                  variants={fadeUp}
                  custom={i}
                  className="rounded-xl border border-slate-800 overflow-hidden hover:border-slate-600 transition-all duration-300"
                >
                  {/* Old Way */}
                  <div className="p-5 bg-red-500/5 border-b border-slate-800">
                    <div className="flex items-center gap-2 mb-3">
                      <X className="h-4 w-4 text-red-400" />
                      <span className="text-sm font-semibold text-red-400">{card.oldLabel}</span>
                    </div>
                    <p className="text-sm text-slate-400 leading-relaxed">{card.oldText}</p>
                  </div>
                  {/* IntentDrive Way */}
                  <div className="p-5 bg-emerald-500/5">
                    <div className="flex items-center gap-2 mb-3">
                      <Check className="h-4 w-4 text-emerald-400" />
                      <span className="text-sm font-semibold text-emerald-400">{card.newLabel}</span>
                    </div>
                    <p className="text-sm text-slate-300 leading-relaxed font-medium">{card.newText}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </section>

        {/* ═══ FEATURES ═══ */}
        <section className="py-24 border-t border-slate-800/50">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0, margin: "0px 0px -50px 0px" }} variants={stagger}>
            <motion.div variants={fadeUp} className="text-center mb-16">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-800 text-slate-400 text-xs font-medium mb-4">Features</div>
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">Built for <span className="text-emerald-400">Affiliate & Lead-Gen Economics</span></h2>
              <p className="text-lg text-slate-400 max-w-2xl mx-auto mt-3">The decision layer between your traffic and your buyers.</p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {[
                { icon: <Target className="h-6 w-6" />, title: 'Dual Scoring Engine', desc: 'Intent Score + Monetization Score run on every session. Two signals, one decision — route to the highest-value path automatically.', gradient: 'from-emerald-500 to-teal-500', border: 'hover:border-emerald-500/30' },
                { icon: <GitBranch className="h-6 w-6" />, title: '3-Path Decision Routing', desc: 'CALL, LEAD, or MULTI-OFFER — the engine picks the optimal path for each user based on score, payout, and buyer availability.', gradient: 'from-blue-500 to-indigo-500', border: 'hover:border-blue-500/30' },
                { icon: <Users className="h-6 w-6" />, title: 'Consumer Profiles & LTV', desc: 'Every session builds a profile. Track Revenue Per User Session (RPUS) across visits, verticals, and conversion events.', gradient: 'from-cyan-500 to-sky-500', border: 'hover:border-cyan-500/30' },
                { icon: <Link2 className="h-6 w-6" />, title: 'Smart Links', desc: 'Auto-detect geo, device, and OS — route users to the right offer, landing page, or app store with zero config.', gradient: 'from-violet-500 to-purple-500', border: 'hover:border-violet-500/30' },
                { icon: <Smartphone className="h-6 w-6" />, title: 'SMS Monetization', desc: 'TCPA-compliant post-conversion SMS flows. Monetize opted-in users with follow-up offers after the initial conversion.', gradient: 'from-amber-500 to-orange-500', border: 'hover:border-amber-500/30' },
                { icon: <Layers className="h-6 w-6" />, title: 'Affiliate Network', desc: 'Built-in rev share, server postbacks, and tracking pixels. Manage affiliates and payouts without a third-party platform.', gradient: 'from-rose-500 to-pink-500', border: 'hover:border-rose-500/30' },
                { icon: <Phone className="h-6 w-6" />, title: 'Buyer Marketplace', desc: 'Native Ringba & TrackDrive integration. Assign DIDs, set concurrency caps, and route live calls to the highest bidder.', gradient: 'from-teal-500 to-emerald-500', border: 'hover:border-teal-500/30' },
                { icon: <Shield className="h-6 w-6" />, title: 'Fraud Detection', desc: '12 real-time fraud rules + IP/device blocklist. Block bots, duplicate sessions, and suspicious patterns before they cost you.', gradient: 'from-red-500 to-rose-500', border: 'hover:border-red-500/30' },
                { icon: <Globe className="h-6 w-6" />, title: 'Landing Page Builder', desc: '11-tab visual editor with live preview. Build, test, and deploy high-converting pages — no code or external tools needed.', gradient: 'from-indigo-500 to-blue-500', border: 'hover:border-indigo-500/30' },
                { icon: <Zap className="h-6 w-6" />, title: 'Lead Intake API', desc: 'Accept inbound leads via API with TrustedForm & Jornaya token validation. Plug into any traffic source in minutes.', gradient: 'from-yellow-500 to-amber-500', border: 'hover:border-yellow-500/30' },
              ].map((f, i) => (
                <motion.div key={f.title} variants={fadeUp} custom={i}
                  className={`group p-6 rounded-xl bg-gradient-to-b from-slate-900/50 to-slate-900/20 border border-slate-800 ${f.border} transition-all duration-300 hover:translate-y-[-2px]`}
                >
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${f.gradient} flex items-center justify-center text-white shadow-lg mb-4`}>
                    {f.icon}
                  </div>
                  <h3 className="text-lg font-semibold mb-2">{f.title}</h3>
                  <p className="text-sm text-slate-400 leading-relaxed">{f.desc}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </section>

        {/* ═══ FULL DASHBOARD SHOWCASE ═══ */}
        <section className="py-24 border-t border-slate-800/50">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0, margin: "0px 0px -50px 0px" }} variants={stagger}>
            <motion.div variants={fadeUp} className="text-center mb-12">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-800 text-slate-400 text-xs font-medium mb-4">Dashboard</div>
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">Real-Time Analytics. <span className="text-emerald-400">Full Visibility.</span></h2>
              <p className="text-lg text-slate-400 max-w-2xl mx-auto">Track every conversation, conversion, and dollar. Know exactly what's working.</p>
            </motion.div>

            <motion.div variants={fadeUp} custom={1} className="rounded-2xl border border-slate-700/60 bg-slate-900/80 backdrop-blur-xl overflow-hidden shadow-2xl shadow-black/40 max-w-6xl mx-auto">
              {/* Dashboard Chrome */}
              <div className="border-b border-slate-800 px-5 py-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-red-500/80" />
                    <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
                    <div className="w-3 h-3 rounded-full bg-green-500/80" />
                  </div>
                  <div className="text-sm text-slate-400 font-medium">IntentDrive Analytics</div>
                </div>
                <div className="flex gap-2">
                  <span className="text-[10px] text-slate-500 bg-slate-800 px-2 py-0.5 rounded">Last 30 days</span>
                  <span className="text-[10px] text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded-full flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />Live</span>
                </div>
              </div>

              <div className="p-5 space-y-4">
                {/* Top stats row */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  <DashStat label="Total Sessions" value="48,291" change="+12%" color="emerald" />
                  <DashStat label="Engaged" value="31,892" change="+18%" color="blue" />
                  <DashStat label="Qualified" value="14,203" change="+24%" color="violet" />
                  <DashStat label="Converted" value="6,847" change="+31%" color="amber" />
                  <DashStat label="Revenue" value="$182.4K" change="+42%" color="rose" />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Main chart */}
                  <div className="md:col-span-2 bg-slate-800/50 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm text-slate-400">Conversions Over Time</span>
                      <div className="flex gap-3 text-[10px]">
                        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500" />Qualified</span>
                        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-500" />Converted</span>
                        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-violet-500" />Revenue</span>
                      </div>
                    </div>
                    <AreaChart />
                  </div>

                  {/* Side panel */}
                  <div className="space-y-4">
                    <div className="bg-slate-800/50 rounded-xl p-4">
                      <span className="text-xs text-slate-500 mb-3 block">Qualification Rate</span>
                      <DonutChart />
                    </div>
                    <div className="bg-slate-800/50 rounded-xl p-4">
                      <span className="text-xs text-slate-500 mb-2 block">Top Campaigns</span>
                      <div className="space-y-2">
                        {[
                          { name: 'Auto Insurance', conv: '34%', leads: '2.1K', color: 'bg-emerald-500' },
                          { name: 'Solar Quotes', conv: '28%', leads: '1.8K', color: 'bg-blue-500' },
                          { name: 'Mortgage Refi', conv: '22%', leads: '1.4K', color: 'bg-violet-500' },
                          { name: 'Health Plans', conv: '19%', leads: '0.9K', color: 'bg-amber-500' },
                        ].map(c => (
                          <div key={c.name} className="flex items-center gap-2 text-xs">
                            <div className={`w-1.5 h-1.5 rounded-full ${c.color}`} />
                            <span className="text-slate-300 flex-1">{c.name}</span>
                            <span className="text-slate-500">{c.leads}</span>
                            <span className="text-emerald-400 font-medium">{c.conv}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Bottom row: funnel + activity */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-slate-800/50 rounded-xl p-4">
                    <span className="text-xs text-slate-500 mb-3 block">Conversion Funnel</span>
                    <ConversionFunnel />
                  </div>
                  <div className="bg-slate-800/50 rounded-xl p-4">
                    <span className="text-xs text-slate-500 mb-3 block">Live Activity</span>
                    <ActivityFeed />
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </section>

        {/* ═══ CHATBOT SHOWCASE — 3 Industries ═══ */}
        <section className="py-24 border-t border-slate-800/50">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0, margin: "0px 0px -50px 0px" }} variants={stagger}>
            <motion.div variants={fadeUp} className="text-center mb-12">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-800 text-slate-400 text-xs font-medium mb-4">Use Cases</div>
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">One Engine. <span className="text-emerald-400">Every Vertical.</span></h2>
              <p className="text-lg text-slate-400 max-w-2xl mx-auto">See how IntentDrive powers conversations across industries</p>
            </motion.div>

            <motion.div variants={fadeUp} custom={1} className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <IndustryChatDemo
                title="Auto Insurance"
                gradient="from-emerald-600 to-teal-600"
                messages={[
                  { text: "Looking for a better rate on auto insurance?", sender: 'bot' },
                  { text: "Yeah mine went up $50 this month", sender: 'user' },
                  { text: "That's frustrating. What's your zip code? I'll find you quotes.", sender: 'bot' },
                  { text: "85281", sender: 'user' },
                  { text: "Great news — 3 carriers have lower rates in your area!", sender: 'bot' },
                ]}
                stats={{ qualified: '34%', avgTime: '47s', leads: '2.1K' }}
              />
              <IndustryChatDemo
                title="Solar Quotes"
                gradient="from-blue-600 to-cyan-600"
                messages={[
                  { text: "Want to see how much you'd save with solar panels?", sender: 'bot' },
                  { text: "How much does it cost?", sender: 'user' },
                  { text: "It depends on your home. Are you a homeowner?", sender: 'bot' },
                  { text: "Yes", sender: 'user' },
                  { text: "Awesome! What's your average electric bill?", sender: 'bot' },
                ]}
                stats={{ qualified: '28%', avgTime: '52s', leads: '1.8K' }}
              />
              <IndustryChatDemo
                title="Mortgage Refi"
                gradient="from-violet-600 to-purple-600"
                messages={[
                  { text: "Rates just dropped. Want to see if you qualify for a refi?", sender: 'bot' },
                  { text: "What's the current rate?", sender: 'user' },
                  { text: "As low as 5.8% for qualified borrowers. What's your current rate?", sender: 'bot' },
                  { text: "6.5%", sender: 'user' },
                  { text: "You could save ~$200/mo! Let me connect you with a lender.", sender: 'bot' },
                ]}
                stats={{ qualified: '22%', avgTime: '61s', leads: '1.4K' }}
              />
            </motion.div>
          </motion.div>
        </section>

        {/* ═══ SOCIAL PROOF ═══ */}
        <section className="py-24 border-t border-slate-800/50">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0, margin: "0px 0px -50px 0px" }} variants={stagger} className="text-center">
            <motion.div variants={fadeUp} className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-800 text-slate-400 text-xs font-medium mb-4">Trusted By</motion.div>
            <motion.h2 variants={fadeUp} custom={1} className="text-3xl font-bold mb-12">Built for Performance Marketers</motion.h2>
            <motion.div variants={fadeUp} custom={2} className="grid grid-cols-2 md:grid-cols-4 gap-8 opacity-40">
              {['InsureTech Co', 'LeadGenius', 'ConvertMax', 'PipelinePro'].map(name => (
                <div key={name} className="text-lg font-bold text-slate-600 tracking-wider">{name}</div>
              ))}
            </motion.div>
          </motion.div>
        </section>

        {/* ═══ Try Demo ═══ */}
        <section className="py-24">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0, margin: "0px 0px -50px 0px" }}
            variants={stagger}
            className="text-center"
          >
            <motion.h2 variants={fadeUp} className="text-3xl font-bold mb-4">
              See the Engine <span className="text-emerald-400">in Action</span>
            </motion.h2>
            <motion.p variants={fadeUp} custom={1} className="text-lg text-slate-400 mb-8 max-w-xl mx-auto">
              Experience how IntentDrive profiles a visitor in seconds and matches them to the highest-value offer — no signup required.
            </motion.p>
            <motion.div variants={fadeUp} custom={2}>
              <Link to="/try">
                <Button size="lg" className="bg-emerald-500 hover:bg-emerald-600 text-white px-10 py-6 text-lg shadow-xl shadow-emerald-500/25 hover:shadow-emerald-500/40 transition-all hover:scale-[1.03]">
                  Try IntentDrive Free <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </motion.div>
          </motion.div>
        </section>

        {/* ═══ TESTIMONIALS ═══ */}
        <section className="py-24 border-t border-slate-800/50">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0, margin: "0px 0px -50px 0px" }} variants={stagger}>
            <motion.div variants={fadeUp} className="text-center mb-12">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-800 text-slate-400 text-xs font-medium mb-4">Testimonials</div>
              <h2 className="text-3xl sm:text-4xl font-bold">Trusted by <span className="text-emerald-400">Performance Marketers</span></h2>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                {
                  quote: 'IntentDrive increased our RPUS by 340% in the first month. The multi-offer stacking is a game changer.',
                  name: 'Jake M.',
                  title: 'Media Buyer',
                  color: 'bg-emerald-500',
                  initials: 'JM',
                },
                {
                  quote: 'We went from $4 per click to $18 per session. The scoring engine picks the right vertical every time.',
                  name: 'Sarah K.',
                  title: 'Affiliate Network Director',
                  color: 'bg-blue-500',
                  initials: 'SK',
                },
                {
                  quote: 'Finally a tool built for performance marketers, not SaaS founders. The Ringba integration alone paid for itself.',
                  name: 'David R.',
                  title: 'Call Center Owner',
                  color: 'bg-violet-500',
                  initials: 'DR',
                },
                {
                  quote: 'The multi-offer stacking alone 3x\'d our revenue per click. Nothing else does this.',
                  name: 'Mike T.',
                  title: 'Performance Network',
                  color: 'bg-cyan-500',
                  initials: 'MT',
                },
                {
                  quote: 'We plugged IntentDrive into our landing pages and saw RPUS jump from $4 to $22 in week one.',
                  name: 'Amanda L.',
                  title: 'Media Agency CEO',
                  color: 'bg-amber-500',
                  initials: 'AL',
                },
                {
                  quote: 'Replaced our entire chat + lead routing stack. One tool, 10x the revenue.',
                  name: 'Chris P.',
                  title: 'Affiliate Manager',
                  color: 'bg-rose-500',
                  initials: 'CP',
                },
              ].map((t, i) => (
                <motion.div
                  key={t.name}
                  variants={fadeUp}
                  custom={i}
                  className="p-6 rounded-xl bg-gradient-to-b from-slate-900/50 to-slate-900/20 border border-slate-800 hover:border-slate-600 transition-all duration-300"
                >
                  {/* Stars */}
                  <div className="flex gap-0.5 mb-4">
                    {Array.from({ length: 5 }).map((_, si) => (
                      <Star key={si} className="h-4 w-4 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                  {/* Quote */}
                  <p className="text-sm text-slate-300 leading-relaxed mb-6">"{t.quote}"</p>
                  {/* Author */}
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full ${t.color} flex items-center justify-center text-white text-sm font-bold`}>
                      {t.initials}
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-white">{t.name}</p>
                      <p className="text-xs text-slate-500">{t.title}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </section>

        {/* ═══ CTA ═══ */}
        <section className="py-24">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0, margin: "0px 0px -50px 0px" }}
            variants={stagger}
            className="relative rounded-2xl border border-emerald-500/20 bg-gradient-to-b from-emerald-500/5 to-transparent p-12 text-center overflow-hidden"
          >
            <div className="absolute inset-0 pointer-events-none" style={{
              backgroundImage: `radial-gradient(circle at 1px 1px, rgba(52,211,153,0.03) 1px, transparent 0)`,
              backgroundSize: '30px 30px',
            }} />
            <motion.h2 variants={fadeUp} className="text-4xl font-bold mb-4 relative">
              Ready to <span className="text-emerald-400">Drive Intent</span>?
            </motion.h2>
            <motion.p variants={fadeUp} custom={1} className="text-lg text-slate-400 mb-8 relative">
              Start building conversion flows in minutes. No credit card required.
            </motion.p>
            <motion.div variants={fadeUp} custom={2} className="relative">
              <Link to="/portal/signup">
                <Button size="lg" className="bg-emerald-500 hover:bg-emerald-600 text-white px-10 py-6 text-lg shadow-xl shadow-emerald-500/25 hover:shadow-emerald-500/40 transition-all hover:scale-[1.03]">
                  Get Started Free <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </motion.div>
          </motion.div>
        </section>
      </main>

      {/* Lead Capture Popup — 15 second delay */}
      <LeadCapturePopup />

      {/* Footer */}
      <footer className="relative z-10 border-t border-slate-800/50 py-12">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <Link to="/" className="flex items-center gap-2">
              <img src="/logo.jpg" alt="IntentDrive.ai" className="h-12 w-auto rounded" style={{ filter: 'brightness(1.2) sepia(1) hue-rotate(100deg) saturate(2)' }} />
              <span className="font-bold">IntentDrive</span>
              <span className="text-xs text-emerald-400">.ai</span>
            </Link>
            <div className="flex items-center gap-6 text-sm text-slate-500">
              <Link to="/compare" className="hover:text-white transition-colors">Compare</Link>
              <Link to="/portal/auth" className="hover:text-white transition-colors">Login</Link>
              <Link to="/portal/signup" className="hover:text-white transition-colors">Sign Up</Link>
              <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
            </div>
            <div className="text-sm text-slate-600">&copy; 2026 IntentDrive.ai</div>
          </div>
        </div>
      </footer>
    </div>
  );
};

function MetricCard({ value, label, icon, color = "text-emerald-400" }: { value: React.ReactNode; label: string; icon: React.ReactNode; color?: string }) {
  return (
    <motion.div variants={fadeUp} className="text-center">
      <div className={`${color} flex justify-center mb-2`}>{icon}</div>
      <div className="text-3xl font-bold text-white">{value}</div>
      <div className="text-sm text-slate-500 mt-1">{label}</div>
    </motion.div>
  );
}

// ─── Dashboard Stat Card ───────────────────────────────────

const dashColors: Record<string, string> = {
  emerald: 'text-emerald-400', blue: 'text-blue-400', violet: 'text-violet-400', amber: 'text-amber-400', rose: 'text-rose-400',
};

function DashStat({ label, value, change, color = 'emerald' }: { label: string; value: string; change: string; color?: string }) {
  return (
    <div className="bg-slate-800/50 rounded-lg p-3">
      <div className="text-[10px] text-slate-500 uppercase tracking-wider">{label}</div>
      <div className="text-xl font-bold text-white mt-1">{value}</div>
      <div className={`text-xs ${dashColors[color]} font-medium`}>{change}</div>
    </div>
  );
}

// ─── Area Chart ────────────────────────────────────────────

function AreaChart() {
  const data1 = [10, 18, 14, 28, 22, 35, 30, 42, 38, 52, 48, 58, 55, 65, 60, 72, 68, 78, 74, 82];
  const data2 = [5, 10, 8, 16, 12, 22, 18, 28, 24, 34, 30, 38, 35, 42, 38, 48, 44, 52, 48, 56];
  const max = 90;
  const w = 600;
  const h = 120;

  const toPath = (data: number[]) => data.map((p, i) => `${i === 0 ? 'M' : 'L'} ${(i / (data.length - 1)) * w} ${h - (p / max) * h}`).join(' ');
  const toArea = (data: number[]) => `${toPath(data)} L ${w} ${h} L 0 ${h} Z`;

  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-32" preserveAspectRatio="none">
      <defs>
        <linearGradient id="areaGrad1" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#10b981" stopOpacity="0.3" />
          <stop offset="100%" stopColor="#10b981" stopOpacity="0" />
        </linearGradient>
        <linearGradient id="areaGrad2" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.2" />
          <stop offset="100%" stopColor="#3b82f6" stopOpacity="0" />
        </linearGradient>
      </defs>
      {/* Grid lines */}
      {[0, 0.25, 0.5, 0.75, 1].map(y => (
        <line key={y} x1="0" y1={y * h} x2={w} y2={y * h} stroke="rgba(148,163,184,0.06)" strokeWidth="1" />
      ))}
      <path d={toArea(data1)} fill="url(#areaGrad1)" />
      <path d={toPath(data1)} fill="none" stroke="#10b981" strokeWidth="2" strokeLinejoin="round" />
      <path d={toArea(data2)} fill="url(#areaGrad2)" />
      <path d={toPath(data2)} fill="none" stroke="#3b82f6" strokeWidth="2" strokeLinejoin="round" />
      <circle cx={w} cy={h - (data1[data1.length - 1] / max) * h} r="3" fill="#10b981" className="animate-pulse" />
      <circle cx={w} cy={h - (data2[data2.length - 1] / max) * h} r="3" fill="#3b82f6" className="animate-pulse" />
    </svg>
  );
}

// ─── Conversion Funnel ─────────────────────────────────────

function ConversionFunnel() {
  const steps = [
    { label: 'Sessions', value: '48,291', pct: 100, color: 'bg-slate-600' },
    { label: 'Engaged', value: '31,892', pct: 66, color: 'bg-blue-500' },
    { label: 'Qualified', value: '14,203', pct: 29, color: 'bg-violet-500' },
    { label: 'Converted', value: '6,847', pct: 14, color: 'bg-emerald-500' },
  ];

  return (
    <div className="space-y-2">
      {steps.map((s, i) => (
        <div key={s.label} className="flex items-center gap-3">
          <div className="w-16 text-right text-[10px] text-slate-500">{s.label}</div>
          <div className="flex-1 h-6 bg-slate-700/30 rounded-full overflow-hidden relative">
            <div
              className={`h-full ${s.color} rounded-full transition-all duration-1000`}
              style={{ width: `${s.pct}%` }}
            />
            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-white font-medium">{s.value}</span>
          </div>
          <div className="w-10 text-right text-[10px] text-slate-500">{s.pct}%</div>
        </div>
      ))}
    </div>
  );
}

// ─── Activity Feed ─────────────────────────────────────────

function ActivityFeed() {
  const [activities] = useState([
    { name: 'Sarah M.', location: 'Austin, TX', action: 'Qualified — Auto Insurance', time: '2s ago', color: 'bg-emerald-500' },
    { name: 'James R.', location: 'Phoenix, AZ', action: 'Converted — Solar Quote', time: '8s ago', color: 'bg-blue-500' },
    { name: 'Maria L.', location: 'Miami, FL', action: 'Qualified — Mortgage Refi', time: '15s ago', color: 'bg-violet-500' },
    { name: 'David K.', location: 'Denver, CO', action: 'Routed — Health Plans', time: '23s ago', color: 'bg-amber-500' },
    { name: 'Emily W.', location: 'Seattle, WA', action: 'Qualified — Auto Insurance', time: '31s ago', color: 'bg-emerald-500' },
    { name: 'Chris P.', location: 'Atlanta, GA', action: 'Converted — Solar Quote', time: '45s ago', color: 'bg-blue-500' },
  ]);

  return (
    <div className="space-y-1.5 max-h-[160px] overflow-hidden">
      {activities.map((a, i) => (
        <div key={i} className="flex items-center gap-2 text-xs py-1.5 px-2 rounded-lg bg-slate-700/20">
          <div className={`w-1.5 h-1.5 rounded-full ${a.color} shrink-0`} />
          <span className="text-white font-medium">{a.name}</span>
          <span className="text-slate-600">·</span>
          <span className="text-slate-500">{a.location}</span>
          <span className="text-slate-400 flex-1 truncate">{a.action}</span>
          <span className="text-slate-600 text-[10px] shrink-0">{a.time}</span>
        </div>
      ))}
    </div>
  );
}

// ─── Industry Chat Demo ────────────────────────────────────

function IndustryChatDemo({ title, gradient, messages, stats }: {
  title: string;
  gradient: string;
  messages: Array<{ text: string; sender: 'bot' | 'user' }>;
  stats: { qualified: string; avgTime: string; leads: string };
}) {
  return (
    <div className="rounded-2xl border border-slate-700/60 bg-slate-900/80 overflow-hidden">
      {/* Header */}
      <div className={`bg-gradient-to-r ${gradient} px-4 py-3 flex items-center gap-2`}>
        <div className="w-7 h-7 rounded-full bg-white/20 flex items-center justify-center">
          <Bot className="h-3.5 w-3.5 text-white" />
        </div>
        <span className="text-sm font-semibold text-white">{title}</span>
        <span className="ml-auto text-[10px] text-white/60 bg-white/10 px-2 py-0.5 rounded-full">Live Demo</span>
      </div>

      {/* Messages */}
      <div className="p-4 space-y-2.5 h-52 overflow-hidden">
        {messages.map((msg, i) => (
          <div key={i} className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[85%] px-3 py-2 rounded-2xl text-xs leading-relaxed ${
              msg.sender === 'user'
                ? 'bg-slate-700 text-white rounded-br-md'
                : 'bg-slate-800 text-slate-200 rounded-bl-md'
            }`}>
              {msg.text}
            </div>
          </div>
        ))}
      </div>

      {/* Stats bar */}
      <div className="border-t border-slate-800 px-4 py-3 grid grid-cols-3 gap-2">
        <div className="text-center">
          <div className="text-sm font-bold text-emerald-400">{stats.qualified}</div>
          <div className="text-[10px] text-slate-500">Qualified</div>
        </div>
        <div className="text-center">
          <div className="text-sm font-bold text-blue-400">{stats.avgTime}</div>
          <div className="text-[10px] text-slate-500">Avg Time</div>
        </div>
        <div className="text-center">
          <div className="text-sm font-bold text-violet-400">{stats.leads}</div>
          <div className="text-[10px] text-slate-500">Leads/mo</div>
        </div>
      </div>
    </div>
  );
}

// ─── Lead Capture Popup ────────────────────────────────────

function LeadCapturePopup() {
  const [show, setShow] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const dismissed = useRef(false);

  useEffect(() => {
    // Don't show if already dismissed or submitted this session
    if (sessionStorage.getItem('intentdrive_popup_dismissed')) return;

    const timer = setTimeout(() => {
      if (!dismissed.current) setShow(true);
    }, 30000);

    return () => clearTimeout(timer);
  }, []);

  const handleClose = () => {
    setShow(false);
    dismissed.current = true;
    sessionStorage.setItem('intentdrive_popup_dismissed', 'true');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    setLoading(true);
    try {
      // Store lead in Supabase via REST API
      const res = await fetch('https://khwvlnkrtrwvfbnnjsrc.supabase.co/rest/v1/landing_leads', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Imtod3ZsbmtydHJ3dmZibm5qc3JjIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzU2OTg3NzcsImV4cCI6MjA5MTI3NDc3N30.dwuWnP6n-mZQZjewpwjXZugvxIJmxKONzhIUV51Eu2g',
          'Authorization': 'Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Imtod3ZsbmtydHJ3dmZibm5qc3JjIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzU2OTg3NzcsImV4cCI6MjA5MTI3NDc3N30.dwuWnP6n-mZQZjewpwjXZugvxIJmxKONzhIUV51Eu2g',
          'Prefer': 'return=minimal',
        },
        body: JSON.stringify({ name: name.trim(), email: email.trim(), source: 'landing_popup' }),
      });
      setSubmitted(true);
      sessionStorage.setItem('intentdrive_popup_dismissed', 'true');
    } catch (err) {
      // Still show success — don't block UX for analytics
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  if (!show) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={handleClose} />

      {/* Modal */}
      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.3, ease: [0.25, 0.4, 0.25, 1] }}
        className="relative w-full max-w-md rounded-2xl border border-slate-700/80 bg-slate-900 shadow-2xl shadow-black/50 overflow-hidden"
      >
        {/* Glow accent */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500" />

        {/* Close button */}
        <button onClick={handleClose} className="absolute top-4 right-4 text-slate-500 hover:text-white transition-colors text-xl leading-none">&times;</button>

        <div className="p-8">
          {!submitted ? (
            <>
              <div className="flex items-center gap-2 mb-2">
                <Target className="h-5 w-5 text-emerald-400" />
                <span className="text-xs text-emerald-400 font-medium tracking-wide uppercase">Early Access</span>
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">Get Early Access</h3>
              <p className="text-slate-400 text-sm mb-6">
                Be the first to know when IntentDrive.ai launches. Join the waitlist and get priority access + exclusive launch pricing.
              </p>

              <form onSubmit={handleSubmit} className="space-y-3">
                <input
                  type="text"
                  placeholder="Your name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl bg-slate-800 border border-slate-700 text-white placeholder-slate-500 text-sm focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition-colors"
                />
                <input
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full px-4 py-3 rounded-xl bg-slate-800 border border-slate-700 text-white placeholder-slate-500 text-sm focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition-colors"
                />
                <button
                  type="submit"
                  disabled={loading || !email.trim()}
                  className="w-full py-3 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-semibold text-sm hover:from-emerald-600 hover:to-teal-600 transition-all shadow-lg shadow-emerald-500/20 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Joining...' : 'Join the Waitlist'}
                </button>
              </form>

              <p className="text-xs text-slate-600 mt-4 text-center">No spam. Unsubscribe anytime.</p>
            </>
          ) : (
            <div className="text-center py-4">
              <div className="w-16 h-16 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-4">
                <Check className="h-8 w-8 text-emerald-400" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">You're on the list!</h3>
              <p className="text-slate-400 text-sm">
                We'll notify you when IntentDrive.ai is ready. You'll get priority access and launch pricing.
              </p>
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}

export default Index;
